"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { Categories, ForumThread } from "@/lib/types";
import { formatDateTimeShort } from "@/lib/format";
import { NextLink } from "@/components/ui/next-link";
import { MessageSquare } from "lucide-react";
import { CreateThreadForm } from "@/components/section-forum/create-thread-form";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ClanTagBadge } from "@/components/ui/clan-tag-badge";
import { Button } from "../ui/button";

type Props = {
  category: Categories;
  threads: ForumThread[];
  isLoggedIn: boolean;
};

export function ForumThreadList({ category, threads, isLoggedIn }: Props) {
  const formatDate = (date: Date | null) => (date ? formatDateTimeShort(date) : "–");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <NextLink href="/home/forum" className="text-sm text-muted-foreground hover:underline mb-1 block">
            ← Forum
          </NextLink>
          <h1 className="text-2xl font-bold">{category.category_name}</h1>
        </div>
        {isLoggedIn && <CreateThreadForm categoryId={category.category_id} />}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4 w-1/2">Thema</TableHead>
                <TableHead className="w-1/6 text-center">Antworten</TableHead>
                <TableHead className="w-1/6">Erstellt von</TableHead>
                <TableHead className="w-1/4">Letzte Aktivität</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {threads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    Noch keine Themen vorhanden. Erstelle das erste!
                  </TableCell>
                </TableRow>
              )}
              {threads.map((thread) => (
                <TableRow key={thread.thread_id} className="hover:bg-muted/50">
                  <TableCell className="pl-4 py-3 relative">
                    <NextLink href={`/home/forum/${category.category_id}/${thread.thread_id}`} className="after:absolute after:inset-0">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="font-medium">{thread.title}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(thread.created_at)}</p>
                        </div>
                      </div>
                    </NextLink>
                  </TableCell>
                  <TableCell className="text-center">{thread.comment_count}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <UserAvatar name={thread.author_name} image={thread.author_image} className="h-12 w-12" />
                      {thread.author_name}
                      <ClanTagBadge tag={thread.author_clan_tag} />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(thread.last_activity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
